import type { CreateStyled } from '@emotion/styled';
import { CurrencyUnits } from './commonVars';

/**
 * emotion/styled에 Transient Props를 지원하기 위한 함수
 * @example const Styled = styled(ELEMENT, transientOptions)`css...`
 */
export const transientOptions: Parameters<CreateStyled>[1] = {
  shouldForwardProp: (propName: string) => !propName.startsWith('$'),
};

/**
 * A wrapper for "JSON.parse()" to support "undefined" value
 */
export const parseJSON = <T>(value: string | null | undefined): T | undefined => {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    return undefined;
  }
};

export const getSortOrderBy = (sort: SortString | SortString[] | undefined) => {
  if (!sort) {
    return undefined;
  }
  if (Array.isArray(sort)) {
    return sort.map(sortString => sortString.split('.')[0])[0];
  }
  return sort.split('.')[0];
};

/**
 * 현재 적용된 sort에서 orderBy의 order를 계산해주는 함수
 */
export const getSortOrder = (sort: SortString | SortString[] | undefined, orderBy: string): SortOrder | undefined => {
  if (!sort) {
    return undefined;
  }

  let sortOrder: SortOrder | undefined;
  if (Array.isArray(sort)) {
    const order = sort.find(sortString => sortString.split('.')[0] === orderBy)?.split('.')[1];
    if (order === 'asc' || order === 'desc' || order === undefined) {
      sortOrder = order;
    }
  } else {
    const [splittedOrderBy, splittedOrder] = sort.split('.');
    sortOrder = splittedOrderBy === orderBy && isOrderString(splittedOrder) ? splittedOrder : undefined;
  }

  return sortOrder;
};

/**
 * 파라미터의 문자열이 'asc', 'desc', undefined인지 확인해주는 타입 가드
 */
export const isOrderString = (str: string | undefined): str is 'asc' | 'desc' | undefined => {
  return str === 'asc' || str === 'desc' || str === undefined;
};

/**
 *
 */
export const isCurrencyUnit = (str: string | undefined): str is CurrencyUnit => {
  for (let i = 0; i < CurrencyUnits.length; i += 1) {
    if (CurrencyUnits[i] === str) {
      return true;
    }
  }

  return false;
};

export const insertAt = (original: string, index: number | undefined, insertString: string): string => {
  const end = index === undefined ? original.length : index > original.length ? original.length : index;
  return original.slice(0, end) + insertString + original.slice(end);
};

/**
 *
 * @param num
 * @param decimalPlaces
 * @returns
 * 
 * 부동소수점 문제의 원인
   자바스크립트는 숫자를 IEEE 754 부동소수점 형식으로 저장한다. 이 형식은 숫자를 정확하게 표현하지 못하는 경우가 있어, 12.95가 실제로는 **12.949999...**처럼 저장될 수 있다.
   toFixed(1)은 실제 값인 12.949999...를 반올림하여 12.9로 결과를 반환한다.

   이와 같은 부동소수점 문제를 피하기 위해 Number.EPSILON을 사용
 * 
 */
export const fixRound = (num: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round((num + Number.EPSILON) * factor) / factor;
};

export const DOCUMENT_FILE_FILTER = '.pdf, .doc, .docx, .xlsx, .xls, image/*';

export const parseFileName = (fullName: string) => {
  const lastDotIndex = fullName.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return { fileName: fullName, fileExtension: '' };
  }

  return {
    fileName: fullName.substring(0, lastDotIndex),
    fileExtension: fullName.substring(lastDotIndex + 1),
  };
};

/**
 *
 * @param str string to clean
 * @returns
 */
export const cleanString = (str: string): string => {
  if (!str) return '';
  return str.replace(/[\b\n\t]/g, '').trim();
};
